/**
 * Utility library to schedule events
 *
 * @author Devin Andrews <dandrews3@wayfair.com>
 * @copyright 2020 Wayfair LLC - All rights reserved
 */
/* eslint-env browser */

// amount of time after page load to run delayed items
const DELAY_TIME = 5000;
// minimum amount of time (ms) we want to have available when running an idle task
const MIN_IDLE_DEADLINE_MS = 5;
// max time(ms) we want to wait for the browser to be idle before running a task
const DEADLINE_TIMEOUT = 200;

const DEFAULT_CONTEXT = typeof window !== 'undefined' ? window : this;

const types = {
  DELAYED: 'DELAYED',
  LOAD: 'LOAD',
  READY: 'READY',
};

const queues = {
  [types.DELAYED]: [],
  [types.LOAD]: [],
  [types.READY]: [],
};

const indicators = {
  [types.DELAYED]: false,
  [types.LOAD]: false,
  [types.READY]: false,
};

/**
 * Adds a task to the specified queue
 *
 * @param {String} type - the name/type of queue to access
 * @param {Function} task - the function to run at "critical" time
 * @param {Object} options - Object containing context, priority and time out
 *                         - context - the context in which to run the task. defaults to window
 *                         - priority - the priority of when the even is to fire. Defaults to DEFAULT_PRIORITY.
 *                                      Lower numbers are fired first
 *                         - timeout - custom time out for when to fire
 */

function addEvent(type, task, options = {}) {
  if (typeof task !== 'function') {
    return;
  }

  const queue = queues[type];
  const indicator = indicators[type];
  const {
    context = DEFAULT_CONTEXT,
    priority = DEFAULT_PRIORITY,
    timeout = DEADLINE_TIMEOUT,
  } = options;

  // if this queue was already run, execute the task immediately
  if (indicator) {
    task.apply(context);
  }

  // add the new task object onto the queue
  queue.push({
    task,
    context,
    priority,
    timeout,
  });
}

/**
 * run all functions in the given queue
 */
function runIdleTasks(type) {
  const queue = queues[type];
  // sort the queue by priority
  queue.sort((a, b) => a.priority - b.priority);
  return new Promise(resolve => {
    const runQueueWhileIdle = deadline => {
      let forceOne = deadline.didTimeout;
      while (
        queue.length > 0 &&
        (forceOne || deadline.timeRemaining() > MIN_IDLE_DEADLINE_MS)
      ) {
        forceOne = false;
        const item = queue.shift();
        // no longer wrapping in try/catch b.c. it swallows errors we want to be surfaced
        item.task.apply(item.context);
      }
      if (queue.length) {
        // if we have been given a timeout here we use that, else use the default one
        const {timeout = DEADLINE_TIMEOUT} = queue[0];
        requestIdleCallback(runQueueWhileIdle, {timeout});
      } else {
        resolve();
      }
    };
    requestIdleCallback(runQueueWhileIdle, {timeout: DEADLINE_TIMEOUT});
  }).then(() => {
    indicators[type] = true;
  });
}

export const SCHEDULER_PRIORITY_ONE = 1;
export const DEFAULT_PRIORITY = 100;
export const LOW_PRIORITY = 1000;

export function queueDelayedTask(task, options) {
  addEvent(types.DELAYED, task, options);
}

export function queuePostLoadTask(task, options) {
  addEvent(types.LOAD, task, options);
}

export function queueDomReadyTask(task, options) {
  addEvent(types.READY, task, options);
}

export default function scheduler() {
  window.addEventListener('load', () => {
    // kick off the postLoad queue, which burns down via requestIdleCallback
    runIdleTasks(types.LOAD);
    // run delayed after a set amount of time after window load
    setTimeout(() => runIdleTasks(types.DELAYED), DELAY_TIME);
  });

  // dynamically import domready to prevent ssr errors
  import('domready').then(({default: domReady}) => {
    const domReadyAsync = () => new Promise(domReady);
    domReadyAsync().then(() => runIdleTasks(types.READY));
  });

  return Promise.resolve();
}
